import { from, of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { AppState } from '../core.state';
import { UserService } from '../entities/user/user.service';
import { LocalizeService } from '../localize/localize.service';
import { NotificationService } from '../notifications/notification.service';
import {
    AcceptInformationAgreement, AcceptInformationAgreementFail, AcceptInformationAgreementSuccess,
    AuthActionTypes, ChangeLanguage, CheckAuth, CheckAuthFail, CheckAuthSuccess, FailActionMessage,
    ForgotPassword, GetLoggedUser, GetLoggedUserFail, GetLoggedUserSuccess, Login, Logout
} from './auth.actions';
import { selectLoggedUser } from './auth.selectors';
import { AuthService } from './auth.service';

export const AUTH_KEY = 'AUTH';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions<Action>,
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private location: Location,
    private userService: UserService,
    private translateService: TranslateService,
    private title: Title,
    private localizeService: LocalizeService
  ) { }

  @Effect({ dispatch: false })
  genericFail$ = this.actions$.pipe(
    ofType<FailActionMessage>(
      // AuthActionTypes.RefreshFail,
      AuthActionTypes.GetLoggedUserFail),
    tap((action: FailActionMessage) => this.notificationService.error(action.payload
      + this.translateService.instant('. La licenza è scaduta, sarai rediretto alla pagina di login.')
    ))
  );

  @Effect({ dispatch: false })
  login$ = this.actions$.pipe(
    ofType<Login>(AuthActionTypes.Login),
    tap(() => this.authService.login())
  );


  @Effect()
  checkAuth$ = this.actions$.pipe(
    ofType<CheckAuth>(AuthActionTypes.CheckAuth),
    mergeMap(() => {
      return from(this.authService.checkAuth()).pipe(
        map(loggedIn => new CheckAuthSuccess(loggedIn)),
        // mergeMap((loggedIn) => {
        //   if (loggedIn) {
        //     return this.userService.me().pipe(
        //       map(u => new CheckAuthSuccess(u)),
        //       catchError(err => of(new CheckAuthFail(err))));
        //   } else {
        //     return of(new CheckAuthSuccess(null));
        //   }
        // }),
        catchError(err => of(new CheckAuthFail(err)))
      );
    })
  );

  @Effect({ dispatch: false })
  checkAuthSuccess$ = this.actions$.pipe(
    ofType<CheckAuthSuccess>(AuthActionTypes.CheckAuthSuccess),
    tap(action => {
      console.log('check auth completed');
    })
  );



  // @Effect()
  // refresh$ = this.actions$.pipe(
  //   ofType<Refresh>(AuthActionTypes.Refresh),
  //   mergeMap((action: Refresh) => {
  //     return from(this.authService.forceRefreshSession()).pipe(
  //       map(() => new RefreshSuccess()),
  //       catchError(err => of(new RefreshFail(err)))
  //     );
  //   })
  // );

  @Effect()
  getLoggedUser$ = this.actions$.pipe(
    ofType<GetLoggedUser>(AuthActionTypes.GetLoggedUser),
    mergeMap(action =>
      this.userService.me().pipe(
        map(result => (new GetLoggedUserSuccess(result))),
        catchError(err => of(new GetLoggedUserFail(err)))
      )
    )
  );

  @Effect({ dispatch: false })
  forgotPassword$ = this.actions$.pipe(
    ofType<ForgotPassword>(AuthActionTypes.ForgotPassword),
    tap(action => this.authService.forgotPassword())
  );

  @Effect({ dispatch: false })
  logout$ = this.actions$.pipe(
    ofType(AuthActionTypes.Logout),
    tap(_ => {
      this.authService.logout();
    })
  );

  @Effect()
  getLoggedUserFail$ = this.actions$.pipe(
    ofType(AuthActionTypes.GetLoggedUserFail),
    debounceTime(3000),
    map((action) => {
      return new Logout();
    })
  );

  @Effect({ dispatch: false })
  logoutLocal$ = this.actions$.pipe(
    ofType(AuthActionTypes.LogoutLocal),
    tap(_ => {
      this.authService.logoutLocal();
    })
  );

  @Effect({ dispatch: false })
  sessionExpired$ = this.actions$.pipe(
    ofType(AuthActionTypes.SessionExpired),
    tap(_ => {
      this.translateService.instant('La sessione è scaduta, sarai rediretto alla pagina di login.');
      this.authService.logoutLocal();
      this.router.navigateByUrl('/login');
    })
  );

  @Effect()
  acceptInformationAgreement$ = this.actions$.pipe(
    ofType<AcceptInformationAgreement>(AuthActionTypes.AcceptInformationAgreement),
    withLatestFrom(this.store.select(selectLoggedUser)),
    mergeMap(([action, loggedUser]) =>
        this.userService.acceptInformationAgreement(loggedUser).pipe(
          map(result => {
            this.store.dispatch(new AcceptInformationAgreementSuccess(result.informationAgreement));
            return new GetLoggedUserSuccess(result);
          } ),
          catchError(err => of(new AcceptInformationAgreementFail(err)))
        )
      )
  );

  @Effect({ dispatch: false })
  changeLanguage$ = this.actions$.pipe(
    ofType<ChangeLanguage>(AuthActionTypes.ChangeLanguage),
    tap(action => this.localizeService.setCurrentLocale(action.languageCode))
  );
}
